import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import memoizeOne from 'memoize-one';
import invariant from 'tiny-invariant';
import {
	type Instruction,
	type ItemMode,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useLocation } from 'react-router-dom';
import { ContainerTreeObject, TreeContextValue, TreeState } from '@/frontend/types/navigationTypes';
import { DependencyContext, TreeContext } from './TreeContext';
import { tree } from './Tree';
import TreeItem from './TreeItem';

type CleanupFn = () => void;

function createTreeItemRegistry() {
    const registry = new Map<string, { element: HTMLElement }>();

    const registerTreeItem = ({
      itemId,
      element,
    }: {
      itemId: string;
      element: HTMLElement;
    }): CleanupFn => {
      registry.set(itemId, { element });
      return () => {
        registry.delete(itemId);
      };
    };

    return { registry, registerTreeItem };
  }

  type DraggableTreeProps = {
    hideProgress?: boolean;
    treeState: TreeState;
    reorder: (targetId: string, movingId: string, action: Instruction) => void;
  };

  export default function DraggableTree({ hideProgress, treeState, reorder }: DraggableTreeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { extractInstruction } = useContext(DependencyContext);
  // const procedure = useProcedureStore((store) => store.procedure);

  const routeLocation = useLocation();

  const [{ registerTreeItem }] = useState(createTreeItemRegistry);

  const { data } = treeState;
  const lastStateRef = useRef<ContainerTreeObject[]>(data);

  useEffect(() => {
    lastStateRef.current = data;
  }, [data]);

  useEffect(() => {
    return () => {
      liveRegion.cleanup();
    };
  }, []);

  /**
   * Returns the items that the item with `itemId` can be moved to.
   *
   * Uses a depth-first search (DFS) to compile a list of possible targets.
   */
  const getMoveTargets = useCallback(({ itemId }: { itemId: string }) => {
    const target = lastStateRef.current;

    const targets = [];

    const searchStack = Array.from(target);
    while (searchStack.length > 0) {
      const node = searchStack.pop();

      if (!node) {
        continue;
      }

      /**
       * If the current node is the item we want to move, then it is not a valid
       * move target and neither are its children.
       */
      if (node.id === itemId) {
        continue;
      }

      /**
       * Draft items cannot have children.
       */
      if (node.isDraft) {
        continue;
      }

      targets.push(node);

      node.children.forEach((childNode) => searchStack.push(childNode));
    }

    return targets;
  }, []);

  const getChildrenOfItem = useCallback((itemId: string) => {
    const lastNode = lastStateRef.current;

    /**
     * An empty string is representing the root
     */
    if (itemId === '') {
      return lastNode;
    }

    const item = tree.find(lastNode, itemId);
    invariant(item);
    return item.children;
  }, []);

  const context = useMemo<TreeContextValue>(
    () => ({
      uniqueContextId: Symbol('unique-id'),
      // memoizing this function as it is called by all tree items repeatedly
      // An ideal refactor would be to update our data shape
      // to allow quick lookups of parents
      getPathToItem: memoizeOne(
        (targetId: string) => tree.getPathToItem({ current: lastStateRef.current, targetId }) ?? [],
      ),
      getMoveTargets,
      getChildrenOfItem,
      registerTreeItem,
    }),
    [getChildrenOfItem, getMoveTargets, registerTreeItem],
  );

  useEffect(() => {
    invariant(ref.current);
    return combine(
      monitorForElements({
        canMonitor: ({ source }) => source.data.uniqueContextId === context.uniqueContextId,
        onDrop(args) {
          const { location, source } = args;
          if (!location.current.dropTargets.length) {
            return;
          }

          if (source.data.type === 'tree-item') {
            const itemId = source.data.id as string;

            const target = location.current.dropTargets[0];
            const targetId = target.data.id as string;

            const instruction: Instruction | null = extractInstruction(target.data);

            if (itemId === targetId && instruction?.type !== 'reparent') {
              return;
            }

            if (instruction !== null) {
              if (instruction.type === 'reorder-above' || instruction.type === 'reorder-below' || instruction.type === 'make-child' || instruction.type === 'reparent') {
                reorder(targetId, itemId, instruction);
              }
            }
          }
        },
      }),
    );
  }, [context, extractInstruction, reorder]);

  return (
    <TreeContext.Provider value={context}>
      <div className='d-flex gap-1 w-100'>
        <div id="tree" ref={ref} className='w-100'>
          {data.map((item, index, array) => {
            const type: ItemMode = (() => {
              if (item.children.length && item.isOpen) {
                return 'expanded';
              }
              if (index === array.length - 1) {
                return 'last-in-group';
              }
              return 'standard';
            })();

            // const dragDisabled = routeLocation.pathname.includes('procedures/run') || routeLocation.pathname.includes('procedures/present') || routeLocation.pathname.includes('procedures/wizard') || !!procedure?.signature || false;
            const dragDisabled = routeLocation.pathname.includes('procedures/run') || routeLocation.pathname.includes('procedures/wizard') || false;
            return <TreeItem item={item} level={0} key={item.id} mode={type} index={index} dragDisabled={dragDisabled} hideProgress={hideProgress} />;
          })}
        </div>
      </div>
    </TreeContext.Provider>
  );
}
